import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getFeatured } from "@/utils/movies";

export default function HeroCarousel() {
  const slides = getFeatured(4);
  const [index, setIndex] = useState(0);
  const timerRef = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      setIndex((next + slides.length) % slides.length);
    },
    [slides.length],
  );

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [slides.length]);

  if (slides.length === 0) return null;

  return (
    <section className="relative -mt-16 md:-mt-[72px] w-full h-[520px] md:h-[660px] overflow-hidden">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title} 焦点剧照`}
            title={`${slide.title} 影视内容`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/90 via-background-50/40 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full flex flex-col justify-end pb-14 md:pb-20 px-4 md:px-10">
        <div className="max-w-2xl hn-fade-up" key={slides[index].id}>
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
              编辑推荐
            </span>
            <span className="px-3 py-1 rounded-full bg-background-100/80 text-foreground-200 text-xs whitespace-nowrap">
              {slides[index].type} · {slides[index].region}
            </span>
          </div>
          <h1 className="font-heading font-black text-3xl md:text-5xl lg:text-6xl text-foreground-50 leading-tight">
            {slides[index].title}
          </h1>
          <p className="mt-3 text-accent-500 font-heading text-base md:text-lg">
            {slides[index].slogan}
          </p>
          <p className="mt-4 text-sm md:text-base text-foreground-300 leading-relaxed line-clamp-2 max-w-xl">
            {slides[index].summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slides[index].id}`}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-background-100/70 hover:bg-background-200 border border-background-300/60 text-foreground-100 text-sm whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-compass-3-line text-lg" />
              浏览全部分类
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-8">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="w-10 h-10 rounded-full bg-background-100/70 hover:bg-primary-500 hover:text-background-50 border border-background-300/60 flex items-center justify-center text-foreground-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="w-10 h-10 rounded-full bg-background-100/70 hover:bg-primary-500 hover:text-background-50 border border-background-300/60 flex items-center justify-center text-foreground-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
          <div className="flex items-center gap-2 ml-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-accent-500" : "w-3 bg-foreground-600/60 hover:bg-foreground-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}